import { Paper } from '@mui/material';
import {
  AssortmentItemsActionToolbar,
  CentralAssortmentTable,
  RemoveFromSubAssortmentDialog,
  useAssortmentItemActions,
} from '@retail/my-assortment/components';
import { AssortmentItemMinimal, CentralAssortmentItem } from '@retail/my-assortment/types';
import {
  BulkEditItemsDialogContainer,
  EditAssortmentTagsDialogContainer,
  EditItemAssortmentTagsDialogContainer,
  MgItemContainer,
  MgItemDialogContainer,
} from '@retail/products/containers';
import { useSelectedSearchParamState } from '@shared/hooks';
import { ReplaceAssortmentItem } from '../../ReplaceAssortmentItem';
import { DeleteItemsDialogContainer, EditAssortmentItemDialog } from '../../AssortmentItems';
import { ExternalItemSource } from '@retail/products/components';
import {
  useCreateOrUpdateSupplierItemAssortmentTags,
  useDeleteSupplierItemAssortmentTag,
} from '@retail/products/data-access';
import { useCallback, useState } from 'react';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@retail/hooks';
import { getISO_8601Date, getLLDate, notBeforeToday } from '@retail/utils';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { useCentralAssortmentItems } from './useCentralAssortmentItems';

interface Props {
  assortmentId: number;
  externalItemSource: ExternalItemSource;
  toggleAddItemsDialog: () => void;
}

export function CentralAssortmentItemsContainer({
  assortmentId,
  externalItemSource,
  toggleAddItemsDialog,
}: Props) {
  const { filteredItems: assortmentItems, isFetching } = useCentralAssortmentItems({
    suspense: false,
    useErrorBoundary: true,
  });

  const t = useMyAssortmentTFunction();
  const queryClient = useQueryClient();
  const toast = useToast();
  const {
    itemsChecked,
    bulkEditPriorities,
    bulkEditTags,
    itemsForDeletion,
    setItemForDeletion,
    resetDeletedItems,
    checkItems,
    resetCheckedItems,
    toggleBulkEditPriorities,
    toggleBulkEditTags,
  } = useAssortmentItemActions((state) => state);

  const { userCanRead } = usePermissionsForCurrentUser();
  const { appTenant } = useSelectedOrgUnit();
  const isPriceCalculationEnabled = userCanRead('PRICE_CALCULATION');
  const isReplaceItemEnabled = useIsFeatureEnabled('replace-item');

  const [selectedItem, setSelectedItem, selectItemById] = useSelectedSearchParamState({
    options: assortmentItems,
    searchParamKey: 'item',
    getOptionId: ({ mgSupplierItemId }) => mgSupplierItemId,
  });

  const [itemToEdit, setItemToEdit] = useState<CentralAssortmentItem | undefined>(undefined);
  const [itemToEditTags, setItemToEditTags] = useState<CentralAssortmentItem | undefined>(
    undefined
  );

  const [supplierItemAssortmentTagToDelete, setSupplierItemAssortmentTagToDelete] = useState<
    { tag: SupplierItemAssortmentTag; mgSupplierItemId: number } | undefined
  >(undefined);

  const [itemToReplace, setItemToReplace] = useState<CentralAssortmentItem | undefined>(undefined);

  const { mutateAsync: deleteSupplierItemFromAssortmentTag, isPending: isDeletingTag } =
    useDeleteSupplierItemAssortmentTag();
  const { mutateAsync: updateSupplierItemAssortmentTag, isPending: isUpdatingTag } =
    useCreateOrUpdateSupplierItemAssortmentTags();

  const handleRemoveAssortmentItemTag = useCallback(
    (tag: SupplierItemAssortmentTag, supplierItemId: number, removedFrom: Date | null) =>
      (removedFrom
        ? updateSupplierItemAssortmentTag({
            urlVariables: { tagId: tag.tagId, assortmentId },
            body: {
              supplierItemIds: [supplierItemId],
              validFrom: getISO_8601Date(tag.validFrom),
              validTo: getISO_8601Date(removedFrom),
            },
          })
        : deleteSupplierItemFromAssortmentTag({
            urlVariables: { tagId: tag.tagId, assortmentId, supplierItemId },
          })
      )
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['central-assortments'] });
          setSupplierItemAssortmentTagToDelete(undefined);
          toast.success(
            removedFrom
              ? t('myAssortment.centralAssortments.itemRemovedFromDate', {
                  assortmentTag: tag.tagName,
                  date: getLLDate(removedFrom),
                })
              : t('myAssortment.actions.deletedItem', {
                  assortmentName: tag.tagName,
                })
          );
        })
        .catch((err: Error) => {
          console.error(err);
          toast.error(t('myAssortment.actions.genericActionError'));
        }),
    [
      assortmentId,
      deleteSupplierItemFromAssortmentTag,
      queryClient,
      t,
      toast,
      updateSupplierItemAssortmentTag,
    ]
  );

  return (
    <Paper sx={{ display: 'flex', flexGrow: 1, overflow: 'auto' }}>
      <CentralAssortmentTable
        items={assortmentItems}
        itemsChecked={itemsChecked}
        checkboxSelection
        loading={isFetching}
        setItemsChecked={checkItems}
        viewItem={selectItemById}
        externalItemSource={externalItemSource}
        showMainSupplierName={appTenant.hasMainSupplier}
        toggleAddItemsDialog={toggleAddItemsDialog}
        deleteAssortmentItemTag={(tag, mgSupplierItemId) =>
          setSupplierItemAssortmentTagToDelete({ tag, mgSupplierItemId })
        }
        editItem={setItemToEdit}
        editTags={setItemToEditTags}
        deleteItem={setItemForDeletion}
        replaceItem={isReplaceItemEnabled ? setItemToReplace : undefined}
      />

      {itemToReplace && (
        <ReplaceAssortmentItem
          open
          assortmentId={assortmentId}
          itemToReplace={itemToReplace}
          onClose={() => setItemToReplace(undefined)}
        />
      )}

      {selectedItem && (
        <MgItemDialogContainer
          open
          onClose={() => setSelectedItem(null)}
          itemNumber={selectedItem.mgItemNumber}
        >
          {({ mgItem }) => (
            <MgItemContainer mgItem={mgItem} showAddItem={false} hiddenBreadcrumbs />
          )}
        </MgItemDialogContainer>
      )}
      {!!itemsForDeletion.length && (
        <DeleteItemsDialogContainer
          assortmentId={assortmentId}
          itemsForDeletion={itemsForDeletion}
          resetCheckedItems={resetDeletedItems}
        />
      )}
      {bulkEditPriorities && (
        <BulkEditItemsDialogContainer
          selectedItems={
            assortmentItems
              .filter((item) => itemsChecked.includes(item.mgSupplierItemId))
              .map((item) => {
                return { ...item, prioritised: item.priority.prioritised };
              }) as AssortmentItemMinimal[]
          }
          onClose={() => toggleBulkEditPriorities(false)}
          resetCheckedItems={resetCheckedItems}
        />
      )}
      {bulkEditTags && (
        <EditAssortmentTagsDialogContainer
          assortmentId={assortmentId}
          selectedItems={
            assortmentItems.filter((item) =>
              itemsChecked.includes(item.mgSupplierItemId)
            ) as AssortmentItemMinimal[]
          }
          onClose={() => toggleBulkEditTags(false)}
          resetCheckedItems={resetCheckedItems}
        />
      )}
      {itemToEdit && (
        <EditAssortmentItemDialog open item={itemToEdit} onClose={() => setItemToEdit(undefined)} />
      )}
      {itemToEditTags && (
        <EditItemAssortmentTagsDialogContainer
          assortmentId={assortmentId}
          item={itemToEditTags as AssortmentItemMinimal}
          onClose={() => setItemToEditTags(undefined)}
        />
      )}
      {supplierItemAssortmentTagToDelete && (
        <RemoveFromSubAssortmentDialog
          tagName={
            supplierItemAssortmentTagToDelete.tag.tagName ??
            String(supplierItemAssortmentTagToDelete.tag.tagId)
          }
          minDate={notBeforeToday(supplierItemAssortmentTagToDelete.tag.validFrom)}
          isSaving={isDeletingTag || isUpdatingTag}
          onConfirm={(removedFrom) =>
            handleRemoveAssortmentItemTag(
              supplierItemAssortmentTagToDelete.tag,
              supplierItemAssortmentTagToDelete.mgSupplierItemId,
              removedFrom
            )
          }
          onClose={() => setSupplierItemAssortmentTagToDelete(undefined)}
        />
      )}

      {!!itemsChecked.length && (
        <AssortmentItemsActionToolbar
          totalItems={assortmentItems.length}
          isUpdatePricesEnabled={isPriceCalculationEnabled}
        />
      )}
    </Paper>
  );
}
